/* global GMAPS_API_KEY, google */
/*
 * PRD-037 — Seccion de direccion de "Configurar comercio" (app SEPARADA, V4 inline).
 *
 * Replica funcionalmente al AddressForm del panel (que esta acoplado a Redux + GoogleMapsContext +
 * @plick/ui), reusando: (a) el mismo loader de Google Places que el resto del portal
 * (useJsApiLoader + GMAPS_API_KEY) y (b) el mismo mapeo de address_components → campos
 * estructurados. Persiste sobre `commerce.address` (con lat/lng) + `direccion`, que son los campos
 * que el SETUP del dueno (`/configuracion-inicial`) espera ya pre-llenados.
 */
import { useRef, type CSSProperties } from 'react';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import type { Libraries } from '@interfaces/Map';
import type { Commerce } from '@interfaces/general';
import { C } from './ui';

const LIBRARIES: Libraries = ['places'];
const FONT = 'Poppins, sans-serif';

type CommerceAddress = NonNullable<Commerce['address']>;

const EMPTY_ADDRESS: CommerceAddress = {
  country: 'Mexico',
  state: '',
  city: '',
  neighborhood: '',
  postalCode: '',
  street: '',
  exteriorNumber: '',
  interiorNumber: '',
  fullAddress: '',
  latitude: 0,
  longitude: 0,
};

interface Props {
  commerce: Commerce;
  handleUpdateValue: <K extends keyof Commerce>(key: K, value: Commerce[K]) => void;
}

const inputStyle: CSSProperties = {
  width: '100%',
  height: 38,
  padding: '0 12px',
  fontSize: 14,
  fontFamily: FONT,
  color: C.primary,
  border: `1px solid ${C.inputBorder}`,
  borderRadius: 6,
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 500,
  color: C.primary,
  marginBottom: 6,
};

// Reconstruye fullAddress a partir de los campos estructurados (mismo formato que el panel).
const buildFullAddress = (a: CommerceAddress): string =>
  `${a.street} ${a.exteriorNumber}${a.interiorNumber ? ` Int. ${a.interiorNumber}` : ''}, ${a.neighborhood}, ${
    a.postalCode
  }, ${a.city}, ${a.state}, ${a.country}`;

export const ConfigureAddressSection = ({ commerce, handleUpdateValue }: Props) => {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GMAPS_API_KEY, libraries: LIBRARIES });
  const acRef = useRef<google.maps.places.Autocomplete | null>(null);

  const addr: CommerceAddress = { ...EMPTY_ADDRESS, ...(commerce.address || {}) };
  const hasCoords = addr.latitude !== 0 && addr.longitude !== 0;

  const handlePlaceChanged = () => {
    const place = acRef.current?.getPlace();
    if (!place) return;
    const components = place.address_components || [];
    const getComponent = (type: string) =>
      components.find((component) => component.types.includes(type))?.long_name || '';
    const loc = place.geometry?.location;

    handleUpdateValue('address', {
      country: getComponent('country') || 'Mexico',
      state: getComponent('administrative_area_level_1'),
      city: getComponent('locality'),
      neighborhood: getComponent('neighborhood') || getComponent('sublocality'),
      postalCode: getComponent('postal_code'),
      street: getComponent('route'),
      exteriorNumber: getComponent('street_number'),
      interiorNumber: '',
      fullAddress: place.formatted_address || '',
      latitude: loc ? loc.lat() : 0,
      longitude: loc ? loc.lng() : 0,
    });
    handleUpdateValue('direccion', place.formatted_address || commerce.direccion || '');
  };

  // Edicion manual de un campo: recalcula fullAddress + direccion (conserva lat/lng capturadas).
  const updateField = (key: keyof CommerceAddress, value: string) => {
    const next: CommerceAddress = { ...addr, [key]: value };
    const fullAddress = buildFullAddress(next);
    handleUpdateValue('address', { ...next, fullAddress });
    handleUpdateValue('direccion', fullAddress);
  };

  // Funcion (no componente) que retorna JSX: evita el remount/perdida de foco que provocaria un
  // componente declarado dentro del render (identidad nueva en cada re-render por keystroke).
  const renderField = (label: string, field: keyof CommerceAddress, placeholder?: string, width?: string | number) => (
    <label style={{ display: 'block', marginBottom: 14, width }}>
      <span style={labelStyle}>{label}</span>
      <input
        style={inputStyle}
        placeholder={placeholder}
        value={(addr[field] as string) || ''}
        onChange={(e) => updateField(field, e.target.value)}
      />
    </label>
  );

  const searchInput = (
    <input
      style={inputStyle}
      placeholder="Escribe y elige la direccion del negocio"
      defaultValue={addr.fullAddress || commerce.direccion || ''}
    />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <p style={{ fontSize: 13, color: C.muted, margin: '0 0 14px' }}>
        Pre-llena la direccion del negocio. El dueno solo la confirmara en su configuracion inicial.
      </p>

      <label style={{ display: 'block', marginBottom: 14 }}>
        <span style={labelStyle}>Buscar direccion</span>
        {isLoaded ? (
          <Autocomplete
            onLoad={(ac) => {
              acRef.current = ac;
            }}
            onPlaceChanged={handlePlaceChanged}
            options={{
              componentRestrictions: { country: 'mx' },
              fields: ['formatted_address', 'geometry', 'address_components'],
            }}
          >
            {searchInput}
          </Autocomplete>
        ) : (
          searchInput
        )}
        {hasCoords && (
          <span style={{ display: 'block', fontSize: 11, color: C.success, marginTop: 6 }}>Ubicacion capturada ✓</span>
        )}
      </label>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {renderField('Codigo postal', 'postalCode', '12345', 120)}
        <div style={{ flex: 1, minWidth: 160 }}>{renderField('Estado', 'state', 'Nuevo Leon')}</div>
      </div>
      {renderField('Ciudad', 'city', 'Monterrey')}
      {renderField('Colonia', 'neighborhood', 'Centro')}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 160 }}>{renderField('Numero exterior', 'exteriorNumber', '110')}</div>
        <div style={{ flex: 1, minWidth: 160 }}>{renderField('Numero interior', 'interiorNumber', 'B (opcional)')}</div>
      </div>
      {renderField('Calle', 'street', 'Calle Principal')}
    </div>
  );
};

export default ConfigureAddressSection;
